import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { MEMBER_TYPE_LABELS } from "@/lib/constants";
import { toast } from "sonner";

export function MemberTypePrompt() {
  const { user, setUser } = useAuth();
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.needs_member_type) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/member-type", { member_type: choice });
      setUser((u) => ({ ...u, ...data }));
      window.dispatchEvent(new Event("member-type-updated"));
      toast.success(choice === "public" ? "Type of membership saved" : "Sent to the National Manager for approval in Profile Approvals");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open>
      <DialogContent className="max-w-md [&>button]:hidden" onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()} data-testid="member-type-prompt">
        <DialogHeader>
          <DialogTitle>What is your type of membership?</DialogTitle>
          <DialogDescription>Please answer this once to keep your membership record correct.</DialogDescription>
        </DialogHeader>
        <RadioGroup value={choice} onValueChange={setChoice} className="space-y-2 mt-2">
          {Object.entries(MEMBER_TYPE_LABELS).map(([key, label]) => (
            <Label key={key} htmlFor={`mt-${key}`} className="flex items-center gap-3 border border-border rounded-md p-3 cursor-pointer has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5">
              <RadioGroupItem id={`mt-${key}`} value={key} data-testid={`member-type-option-${key}`} />
              <span className="font-medium">{label}</span>
            </Label>
          ))}
        </RadioGroup>
        {choice && choice !== "public" && (
          <p className="text-xs text-muted-foreground" data-testid="member-type-approval-note">
            A National Manager must approve this change in Profile Approvals. If approved, your membership number will change to match the new type.
          </p>
        )}
        <div className="mt-2">
          <Button onClick={save} disabled={!choice || busy} className="w-full" data-testid="member-type-save">{busy ? "Saving…" : "Save answer"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
